import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Highlighter } from "react-bootstrap-typeahead";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import Button from "components/atoms/Button/Button";
import InputField from "components/atoms/InputField/InputField";
import { ReactComponent as DeleteIcon } from "assets/images/icons/Delete.svg";
import { useGetCountries } from "api/queries/common/useGetCountries";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { trackQuotePackingCommodityAdded } from "pages/shipper/RequestForm/formEvents/requestFormAnalytics";
import SearchSelect from "../../Common/SearchSelect";
import { ICurrencyOption, IDefaultCurrencyOption } from "../Types";
import FileAttachment from "./FileAttachment";

interface CustomsInformationProps {
  currencyOptions: IDefaultCurrencyOption[];
  getRequestFormValues: any;
  state: RequestFormState;
  mode: "manual" | "file";
  selectedItemIndex?: number | null;
  isQuoteRequest?: boolean;
}

function CustomsInformation({
  currencyOptions,
  getRequestFormValues,
  state,
  mode,
  selectedItemIndex = null,
  isQuoteRequest = false,
}: CustomsInformationProps) {
  const intl = useIntl();
  const { control, setValue, getValues, clearErrors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "good_customs_information",
  });
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  // Origin country is stored as the ISO alpha-3 code (the API's `value`).
  const { data: countryOptions = [] } = useGetCountries({ useV2: true, refetchOnMount: false });

  const initialGoodCustomsInfo = {
    hs_code: "",
    origin_country: "",
    value: "",
    description: "",
    currency_code: getValues("good_customs_information.0.currency_code") || state.locationDetails?.default_currency || "",
  };

  // Add the initial row of good customs information, and enable additional instructions if they exist
  useEffect(() => {
    if (fields.length === 0 && mode === "manual") append(initialGoodCustomsInfo);
  }, [fields]);

  // Clear error message if parcel is not selected in regular freight mode and custom parcel mode
  useEffect(() => {
    if (!isParcelTypeSelected()) {
      clearErrors("good_customs_information");
    }
  }, [currentActiveStep]);

  const handleCurrencyCodeBlur = (index: number) => {
    const currencyCode = getValues(`good_customs_information.${index}.currency_code`);
    if (!currencyCode) {
      setValue(`good_customs_information.${index}.currency_code`, state.locationDetails?.default_currency || "");
    }
  };

  /**
   * Renders the currency option for the dropdown menu
   * @param param0 - the currency option and the search text
   * @returns {JSX.Element} - the currency option
   */
  const renderCurrencyOptions = ({ option, text }: { option: ICurrencyOption; text: string }) => (
    <div className="p-2">
      <div className="font-weight-bold font-12">
        <Highlighter search={text}>{`(${option.code}) ${option.label}`}</Highlighter>
      </div>
    </div>
  );

  /**
   * Filters the currency options based on the search text
   * @param option - the currency option
   * @param itemProps - the item props
   * @returns {boolean} true if the currency option matches the search text
   */
  const filterCurrency = (option: any, itemProps: any) => {
    if (itemProps.selected.length) {
      return true;
    }
    return (
      option.label.toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1 ||
      option.code.toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1
    );
  };

  /**
   * Checks if any of the freight modes selected is parcel
   * @returns {boolean} true if any of the freight modes selected is parcel
   */

  const isParcelTypeSelected = () => {
    const parcelSelected = Boolean(getRequestFormValues("invited_modes.parcel"));
    let customParcelSelected = false;
    const customFreightModes = getRequestFormValues("invited_modes.custom");
    if (Array.isArray(customFreightModes) && customFreightModes.length) {
      const customFreightModeValues = customFreightModes.filter((item) => item.value).map((item) => `custom_${item.key}`);
      customParcelSelected = customFreightModeValues.some(
        (item) => state.freightModes?.available_modes?.[item]?.type === "parcel",
      );
    }
    return parcelSelected || customParcelSelected;
  };

  return (
    <div className="mt-lg">
      {mode === "manual" &&
        fields &&
        fields?.map((item, i) => (
          <div className="d-flex mt-2" key={item.id}>
            {/* Commodity information */}
            <div className="flex-row packing-list-add-item-input-xl">
              <InputField
                label={
                  fields.length > 0 && i === 0
                    ? intl.formatMessage({
                        id: "new-request.packing-list.commodityInformation",
                        defaultMessage: "Commodity information",
                      })
                    : ""
                }
                placeholder="Item description"
                field_id={`good_customs_information.${i}.description`}
                validations={{
                  setValueAs: (v: string) => (!v ? "" : v),
                  shouldUnregister: true,
                }}
                required={isParcelTypeSelected()}
              />
            </div>
            {/* Origin country */}
            <div className="w-192 ml-2">
              {fields.length > 0 && i === 0 && (
                <label>
                  <FormattedMessage id="new-request.packing-list.originCountry" defaultMessage="Origin Country" />
                </label>
              )}
              <SearchSelect
                name={`good_customs_information.${i}.origin_country`}
                options={countryOptions}
                placeholder={intl.formatMessage({
                  id: "new-request.packing-list.originCountryPlaceholder",
                  defaultMessage: "Country",
                })}
                preselect={false}
                shouldUnregister
              />
            </div>
            {/* HS Code */}
            <div className="ml-2 flex-row packing-list-add-item-input-large">
              <InputField
                label={
                  fields.length > 0 && i === 0
                    ? intl.formatMessage({
                        id: "new-request.packing-list.hsCode",
                        defaultMessage: "HS Code",
                      })
                    : ""
                }
                placeholder="HS Code"
                field_id={`good_customs_information.${i}.hs_code`}
                validations={{
                  setValueAs: (v: string) => (!v ? null : v),
                  shouldUnregister: true,
                }}
                required={isParcelTypeSelected()}
              />
            </div>
            {/* Value */}
            <div className="ml-2 flex-row packing-list-add-item-input-small">
              <InputField
                label={
                  fields.length > 0 && i === 0
                    ? intl.formatMessage({
                        id: "new-request.packing-list.customCommodityValue",
                        defaultMessage: "Value",
                      })
                    : ""
                }
                placeholder="Value"
                field_id={`good_customs_information.${i}.value`}
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                  shouldUnregister: true,
                }}
                required={isParcelTypeSelected()}
              />
            </div>
            {/* Currency */}
            <div className="w-192 ml-2 packing-list-add-item-input-large">
              {fields.length > 0 && i === 0 && (
                <label className={`${isParcelTypeSelected() ? "required" : ""}`}>
                  <FormattedMessage id="general.currency" defaultMessage="Currency" />
                </label>
              )}
              <SearchSelect
                name={`good_customs_information.${i}.currency_code`}
                options={
                  currencyOptions?.length > 0
                    ? currencyOptions.map((currency: IDefaultCurrencyOption) => ({
                        id: currency.id,
                        label: currency.name,
                        code: currency.code,
                        value: currency.code,
                      }))
                    : []
                }
                onSelect={(v: any) => {
                  if (!v?.[0]?.code) {
                    setValue(`good_customs_information.${i}.currency_code`, v?.[0]?.code);
                  } else {
                    fields.forEach((item, index) => {
                      setValue(`good_customs_information.${index}.currency_code`, v?.[0]?.code);
                    });
                  }
                }}
                handleOnBlur={() => handleCurrencyCodeBlur(i)}
                filterBy={filterCurrency}
                placeholder={intl.formatMessage({ id: "general.currency", defaultMessage: "Currency" })}
                handleRenderOption={renderCurrencyOptions}
                capitalizeOptions
                shouldUnregister
                required={isParcelTypeSelected()}
              />
            </div>
            {/* Delete button */}
            {fields.length > 1 && (
              <div className="d-flex ml-2 cursor-pointer mb-2" style={{ marginTop: i === 0 ? 28 : 5 }}>
                <DeleteIcon onClick={() => remove(i)} />
              </div>
            )}
          </div>
        ))}
      {/* Add commodity button */}
      {mode === "manual" && (
        <Button
          variant="link"
          className="text-primary mt-lg py-0"
          onClick={() => {
            append(initialGoodCustomsInfo);
            if (!isQuoteRequest) return;

            const packingList = getRequestFormValues("packing_list") || [];
            // Nested add-item form holds the current row; exclude that row from packing_list when editing.
            const commoditiesInOtherRows = packingList.reduce(
              (sum: number, row: { good_customs_information?: unknown[] }, index: number) => {
                if (selectedItemIndex != null && index === selectedItemIndex) return sum;
                return sum + (row.good_customs_information?.length ?? 0);
              },
              0,
            );
            const commoditiesInCurrentRow = (getValues("good_customs_information") || []).length + 1;
            trackQuotePackingCommodityAdded(commoditiesInOtherRows + commoditiesInCurrentRow);
          }}
        >
          + <FormattedMessage id="new-request.packing-list.addAnotherCommodity" defaultMessage="add another commodity" />
        </Button>
      )}
      {mode === "file" && <FileAttachment state={state} />}
    </div>
  );
}

export default CustomsInformation;
